(() => {
  const forms = document.querySelectorAll('[data-header-search]');

  if (!forms.length) return;

  const initializedForms = new WeakSet();
  const formStates = new WeakMap();
  const minimumQueryLength = 2;
  const trendingWindow = '12h';
  const trendingRequestTimeoutMs = 2500;
  const featuredProductsRequestTimeoutMs = 5000;
  const dictionaryRequestTimeoutMs = 5000;
  const suggestionRequestTimeoutMs = 7000;
  const outsideClickOpenGraceMs = 220;
  const suggestedSearchLimit = 4;
  const defaultTerms = ['Dresses', 'Jeans', 'Tops', 'Shoes', 'New Arrivals', 'Sale'];
  const dictionaryCache = new Map();
  const featuredProductRequests = new Map();
  const productSuggestionCategories = [
    'jeans',
    'shorts',
    'dresses',
    'dress',
    'tops',
    'top',
    'skirts',
    'skirt',
    'pants',
    'sweaters',
    'sweater',
    'cardigans',
    'cardigan',
    'jackets',
    'jacket',
    'bodysuits',
    'bodysuit',
    'rompers',
    'romper',
    'jumpsuits',
    'jumpsuit',
    'sandals',
    'boots',
    'sneakers',
    'heels',
    'bags',
    'bag'
  ];
  const productSuggestionModifiers = [
    'low rise',
    'mid rise',
    'high rise',
    'wide leg',
    'straight leg',
    'barrel',
    'baggy',
    'cargo',
    'distressed',
    'denim',
    'dark wash',
    'medium wash',
    'light wash',
    'mini',
    'midi',
    'maxi',
    'curvy'
  ];

  const getActiveTrendingTerms = (state) => {
    return state?.trendingTerms?.length ? state.trendingTerms : state?.fallbackTerms || defaultTerms;
  };

  const escapeHtml = (value) => {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const normalizeEndpoint = (endpoint) => String(endpoint || '').trim().replace(/\/+$/, '');

  const isModalSearch = (form) => form.dataset.headerSearchMode === 'modal';

  const normalizeSearchText = (value) => {
    return String(value || '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const normalizeHandle = (value) => {
    const raw = String(value || '').trim();
    const pathMatch = raw.match(/\/collections\/([^/?#]+)/i);
    const handle = pathMatch ? pathMatch[1] : raw;

    return handle
      .replace(/^gid:\/\/shopify\/Collection\//i, '')
      .split(/[?#]/)[0]
      .trim()
      .toLowerCase();
  };

  const normalizeNumericId = (value) => {
    const raw = String(value || '').trim();
    const match = raw.match(/(?:Collection\/)?(\d+)$/i);

    return match ? match[1] : '';
  };

  const compactSearchText = (value) => normalizeSearchText(value).replace(/\s+/g, '');

  const titleCaseSmallWords = new Set([
    'a',
    'an',
    'and',
    'as',
    'at',
    'but',
    'by',
    'for',
    'from',
    'in',
    'into',
    'my',
    'of',
    'on',
    'or',
    'the',
    'to',
    'via',
    'with'
  ]);

  const titleCaseTerm = (value) => {
    return normalizeSearchText(value)
      .split(' ')
      .filter(Boolean)
      .map((word, index) => {
        if (index > 0 && titleCaseSmallWords.has(word)) return word;

        return `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
      })
      .join(' ');
  };

  const createRequestTimeout = (controller, timeoutMs) => {
    let timeoutId = 0;
    const timeout = {
      timedOut: false,
      clear: () => window.clearTimeout(timeoutId)
    };

    timeoutId = window.setTimeout(() => {
      timeout.timedOut = true;
      controller.abort();
    }, timeoutMs);

    return timeout;
  };

  const getRequestId = () => {
    return window.crypto?.randomUUID?.() || `request-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  const getWorkerSessionId = () => {
    return window.WobWorkerAnalytics?.getSessionId?.() || '';
  };

  const normalizeImageUrl = (url) => {
    if (!url) return '';

    return String(url).startsWith('//') ? `${window.location.protocol}${url}` : String(url);
  };

  const withImageWidth = (url, width) => {
    const normalizedUrl = normalizeImageUrl(url);
    if (!normalizedUrl) return '';

    try {
      const parsedUrl = new URL(normalizedUrl, window.location.origin);
      parsedUrl.searchParams.set('width', String(width));
      return parsedUrl.toString();
    } catch (error) {
      const separator = normalizedUrl.includes('?') ? '&' : '?';

      return `${normalizedUrl}${separator}width=${width}`;
    }
  };

  const moneyAmount = (money) => {
    const amount = Number(money?.amount ?? money ?? 0);

    return Number.isFinite(amount) ? amount : 0;
  };

  const formatMoney = (amount, currencyCode = 'USD') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currencyCode
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const getProductUrl = (product) => {
    if (product?.url) return product.url;
    if (product?.handle) return `/products/${encodeURIComponent(product.handle)}`;

    return '#';
  };

  const getCollectionUrl = (collection) => {
    if (collection?.url) return collection.url;
    if (collection?.handle) return `/collections/${encodeURIComponent(collection.handle)}`;

    return '#';
  };

  const getImage = (item) => {
    const image = item?.featuredImage || item?.featured_image || item?.image || null;

    if (typeof image === 'string') {
      return {
        url: image,
        altText: item?.title || '',
        width: 220,
        height: 330
      };
    }

    if (image?.url) {
      return {
        ...image,
        altText: image.altText || image.alt || item?.title || ''
      };
    }

    return image;
  };

  const normalizeProducts = (payload) => {
    return payload?.products
      || payload?.results?.products
      || payload?.data?.products
      || payload?.resources?.results?.products
      || [];
  };

  const normalizeCollections = (payload) => {
    return payload?.collections
      || payload?.results?.collections
      || payload?.data?.collections
      || payload?.resources?.results?.collections
      || [];
  };

  const normalizeQuerySuggestions = (payload) => {
    const source = payload?.querySuggestions
      || payload?.queries
      || payload?.results?.queries
      || payload?.data?.queries
      || payload?.resources?.results?.queries
      || [];

    return source.map((item) => {
      return typeof item === 'string' ? item : item?.term || item?.query || item?.text || item?.value || '';
    }).map((term) => String(term || '').trim()).filter(Boolean);
  };

  const getFallbackTerms = (form) => {
    const script = form.querySelector('[data-header-search-fallbacks]');

    if (!script?.textContent) return defaultTerms;

    try {
      const parsed = JSON.parse(script.textContent);
      const terms = parsed.map((term) => String(term || '').trim()).filter(Boolean);

      return terms.length ? terms : defaultTerms;
    } catch (error) {
      return defaultTerms;
    }
  };

  const getFeaturedProducts = (form) => {
    const script = form.querySelector('[data-header-search-featured-products]');

    if (!script?.textContent) return [];

    try {
      const parsed = JSON.parse(script.textContent);

      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch (error) {
      return [];
    }
  };

  const isRedoCollection = (collection) => {
    const text = normalizeSearchText(`${collection?.handle || ''} ${collection?.title || ''}`);

    if (text.includes('package protection')) return true;
    if (text.includes('return protection')) return true;
    if (text.includes('redo coverage')) return true;

    return text.includes('redo') && (
      text.includes('return')
      || text.includes('exchange')
      || text.includes('protection')
      || text.includes('coverage')
    );
  };

  const normalizeMenuCollection = (collection) => {
    const handle = normalizeHandle(collection?.handle || collection?.url);
    const title = String(collection?.title || handle || '').trim();

    if (!handle || !title || isRedoCollection({ ...collection, handle, title })) return null;

    return {
      ...collection,
      handle,
      title,
      term: title,
      normalized: normalizeSearchText(title),
      compact: compactSearchText(title),
      numericId: normalizeNumericId(collection?.numericId || collection?.id),
      url: collection?.url || `/collections/${encodeURIComponent(handle)}`,
      productCount: Number(collection?.productCount || collection?.productsCount || collection?.count || 0),
      type: 'collection',
      weight: 700 + Math.min(300, Number(collection?.productCount || collection?.productsCount || collection?.count || 0))
    };
  };

  const getMenuCollections = (form) => {
    const script = form.querySelector('[data-header-search-menu-collections]');

    if (!script?.textContent) return [];

    try {
      const parsed = JSON.parse(script.textContent);
      const seen = new Set();

      return (Array.isArray(parsed) ? parsed : [])
        .map(normalizeMenuCollection)
        .filter(Boolean)
        .filter((collection) => {
          const key = collection.numericId || collection.handle;

          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
    } catch (error) {
      return [];
    }
  };

  const normalizeTrendingTerms = (payload, fallbackTerms) => {
    const source = payload?.trendingSearches
      || payload?.trending
      || payload?.searches
      || payload?.terms
      || [];
    const terms = source.map((item) => {
      return typeof item === 'string' ? item : item?.term || item?.query || item?.value || '';
    }).map((term) => String(term || '').trim()).filter(Boolean);

    return terms.length ? terms : fallbackTerms;
  };

  const normalizeDictionaryEntries = (payload) => {
    const entries = Array.isArray(payload?.entries) ? payload.entries : [];

    return entries.map((entry) => {
      const term = String(entry?.term || entry?.title || '').trim();
      const normalized = entry?.normalized || normalizeSearchText(term);

      return {
        ...entry,
        term,
        title: entry?.title || term,
        normalized,
        compact: entry?.compact || compactSearchText(term),
        type: entry?.type || 'query',
        url: entry?.url || ''
      };
    }).filter((entry) => entry.term && entry.normalized.length >= 2);
  };

  const dictionaryTypeWeight = (type) => {
    if (type === 'collection') return 40;
    if (type === 'brand') return 36;
    if (type === 'query') return 32;
    if (type === 'product') return 18;

    return 0;
  };

  const tokenDistance = (left, right) => {
    const a = String(left || '');
    const b = String(right || '');

    if (a === b) return 0;
    if (Math.abs(a.length - b.length) > 1) return 2;
    if (a.length < 4 || b.length < 4) return 2;

    let edits = 0;
    let i = 0;
    let j = 0;

    while (i < a.length && j < b.length) {
      if (a[i] === b[j]) {
        i += 1;
        j += 1;
      } else {
        edits += 1;
        if (edits > 1) return edits;

        if (a.length > b.length) {
          i += 1;
        } else if (b.length > a.length) {
          j += 1;
        } else {
          i += 1;
          j += 1;
        }
      }
    }

    if (i < a.length || j < b.length) edits += 1;

    return edits;
  };

  const softTokenScore = (entryText, queryTokens) => {
    const entryWords = entryText.split(' ').filter(Boolean);

    return queryTokens.reduce((score, token) => {
      if (entryWords.includes(token)) return score + 34;

      if (entryWords.some((word) => word === `${token}s` || word === `${token}es`)) {
        return score + 26;
      }

      if (entryWords.some((word) => tokenDistance(word, token) <= 1)) {
        return score + 18;
      }

      if (token.length >= 4 && entryWords.some((word) => word.startsWith(token) && !word.startsWith(`${token}n`))) {
        return score + 12;
      }

      return score;
    }, 0);
  };

  const scoreDictionaryEntry = (entry, query) => {
    const normalizedQuery = normalizeSearchText(query);
    if (!normalizedQuery) return 0;

    const entryText = entry.normalized || normalizeSearchText(entry.term);
    const entryCompact = entry.compact || compactSearchText(entry.term);
    const queryCompact = compactSearchText(query);
    const tokens = normalizedQuery.split(' ').filter(Boolean);
    const paddedEntryText = ` ${entryText} `;
    let score = 0;

    if (entryText === normalizedQuery) score += 1000;
    if (entryText.startsWith(`${normalizedQuery} `)) score += 700;
    if (paddedEntryText.includes(` ${normalizedQuery} `)) score += 360;
    if (queryCompact.length >= 2 && entryCompact.startsWith(queryCompact) && !entryCompact.startsWith(`${queryCompact}n`)) score += 240;
    if (queryCompact.length >= 5 && entryCompact.includes(queryCompact)) score += 120;

    tokens.forEach((token) => {
      if (entryText.split(' ').includes(token)) {
        score += 44;
      }
    });

    score += softTokenScore(entryText, tokens);

    if (score <= 0) return 0;

    return score
      + dictionaryTypeWeight(entry.type)
      + Math.min(90, Number(entry.weight || 0) / 10)
      + Math.min(35, Number(entry.count || entry.productCount || 0) / 25);
  };

  const getDictionaryMatches = (state, query, types, limit) => {
    const entries = state?.dictionaryEntries || [];
    const typeSet = new Set(types);
    const seen = new Set();

    return entries
      .map((entry) => ({
        ...entry,
        dictionaryScore: typeSet.has(entry.type) ? scoreDictionaryEntry(entry, query) : 0
      }))
      .filter((entry) => entry.dictionaryScore > 0)
      .sort((a, b) => {
        return b.dictionaryScore - a.dictionaryScore
          || String(a.term).localeCompare(String(b.term));
      })
      .filter((entry) => {
        const key = `${entry.type}:${entry.normalized}`;

        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, limit);
  };

  const productMatchKey = (product) => {
    return normalizeNumericId(product?.numericId || product?.id)
      || String(product?.handle || '').trim().toLowerCase()
      || normalizeSearchText(product?.title || product?.term);
  };

  const isLiteralProductTitleMatch = (product, query) => {
    const normalizedQuery = normalizeSearchText(query);
    const normalizedTitle = normalizeSearchText(product?.title || product?.term);

    if (!normalizedQuery || !normalizedTitle) return false;

    return normalizedTitle === normalizedQuery || normalizedTitle.startsWith(`${normalizedQuery} `);
  };

  const hasLiteralProductTitleMatch = (state, query) => {
    return (state?.dictionaryEntries || []).some((entry) => {
      return entry.type === 'product' && isLiteralProductTitleMatch(entry, query);
    });
  };

  const getDefaultFeaturedProducts = (state) => {
    if (state?.featuredProductsStatus === 'ready' && state.workerFeaturedProducts?.length) {
      return state.workerFeaturedProducts;
    }

    return Array.isArray(state?.featuredProducts) ? state.featuredProducts : [];
  };

  const selectProductsForQuery = (state, products, query, limit) => {
    const source = Array.isArray(products) ? products : [];
    const sourceByKey = new Map(
      source.map((product) => [productMatchKey(product), product]).filter(([key]) => Boolean(key))
    );
    const dictionaryMatches = getDictionaryMatches(state, query, ['product'], limit)
      .map((product) => sourceByKey.get(productMatchKey(product)))
      .filter(Boolean);
    const featuredProducts = getDefaultFeaturedProducts(state);
    const seen = new Set();
    const literalMatches = source.filter((product) => isLiteralProductTitleMatch(product, query));
    const rankedMatches = [];
    const appendUniqueProducts = (candidates) => {
      for (const product of candidates) {
        if (rankedMatches.length >= limit) break;

        const key = productMatchKey(product);

        if (!key || seen.has(key)) continue;
        seen.add(key);
        rankedMatches.push(product);
      }
    };

    // Keep the four-card v2 rail full without letting a broader worker query
    // displace products whose names directly resemble the customer's query.
    // Broader and merchant-selected products remain as last-resort backfill.
    appendUniqueProducts(literalMatches);
    appendUniqueProducts(dictionaryMatches);
    appendUniqueProducts(source);
    appendUniqueProducts(featuredProducts);

    return rankedMatches.slice(0, limit);
  };

  const filterCollectionsForMenu = (state, collections) => {
    const source = (Array.isArray(collections) ? collections : []).filter((collection) => !isRedoCollection(collection));
    const handles = state?.menuCollectionHandles;
    const numericIds = state?.menuCollectionNumericIds;

    if (!handles?.size && !numericIds?.size) return source;

    return source.filter((collection) => {
      const handle = normalizeHandle(collection?.handle || collection?.url);
      const numericId = normalizeNumericId(collection?.numericId || collection?.id);

      return (handle && handles.has(handle)) || (numericId && numericIds.has(numericId));
    });
  };

  const dedupeCollections = (collections, limit) => {
    const seen = new Set();

    return collections
      .sort((a, b) => {
        return Number(b.dictionaryScore || b.searchScore || b.weight || 0) - Number(a.dictionaryScore || a.searchScore || a.weight || 0)
          || Number(b.count || b.productCount || 0) - Number(a.count || a.productCount || 0)
          || String(a.term || a.title || a.handle || '').localeCompare(String(b.term || b.title || b.handle || ''));
      })
      .filter((entry) => {
        const key = normalizeNumericId(entry.numericId || entry.id) || normalizeHandle(entry.handle || entry.url) || entry.normalized || normalizeSearchText(entry.term);

        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, limit);
  };

  const getMenuCollectionMatches = (state, query, limit) => {
    const collections = state?.menuCollections || [];
    const normalizedQuery = normalizeSearchText(query);

    if (!collections.length) return [];

    if (!normalizedQuery) {
      return dedupeCollections([...collections], limit);
    }

    return dedupeCollections(collections.map((collection) => ({
      ...collection,
      dictionaryScore: scoreDictionaryEntry(collection, normalizedQuery)
    })).filter((collection) => collection.dictionaryScore > 0), limit);
  };

  const getCollectionMatches = (state, query, limit) => {
    const menuMatches = getMenuCollectionMatches(state, query, limit);
    const dictionaryMatches = filterCollectionsForMenu(state, getDictionaryMatches(state, query, ['collection'], limit));

    return dedupeCollections([...menuMatches, ...dictionaryMatches], limit);
  };

  const getFallbackCollectionMatches = (state, limit) => {
    const menuMatches = getMenuCollectionMatches(state, '', limit);

    if (menuMatches.length) return menuMatches;

    return dedupeCollections(filterCollectionsForMenu(state, (state?.dictionaryEntries || []).filter((entry) => entry.type === 'collection')), limit);
  };

  const productSuggestionCategoryFor = (normalizedTitle) => {
    const words = normalizedTitle.split(' ').filter(Boolean);

    return productSuggestionCategories.find((category) => {
      return words.includes(category) || words.includes(`${category}s`);
    }) || '';
  };

  const derivedProductSuggestionTerms = (entry) => {
    const normalizedTitle = normalizeSearchText(String(entry?.term || entry?.title || '').split(' - ')[0]);
    const words = normalizedTitle.split(' ').filter(Boolean);
    const category = productSuggestionCategoryFor(normalizedTitle);
    const styleName = words[0] || '';
    const terms = new Set();

    if (category) {
      terms.add(category);
    }

    if (styleName.length >= 3 && category) {
      terms.add(`${styleName} ${category}`);
    }

    productSuggestionModifiers.forEach((modifier) => {
      if (!normalizedTitle.includes(modifier)) return;

      terms.add(modifier);
      if (category) terms.add(`${modifier} ${category}`);
    });

    if (words.length >= 3) {
      const withoutStyleName = words.slice(1).join(' ');
      const trimmedWords = withoutStyleName.split(' ');

      if (trimmedWords.length >= 2 && trimmedWords.length <= 4) {
        terms.add(withoutStyleName);
      }
    }

    if (entry?.type === 'product' && category) {
      const brandMatch = normalizedTitle.match(/\b(risen|kancan|kan can|flying monkey|vero moda|molly bracken)\b/);

      if (brandMatch) terms.add(`${brandMatch[1]} ${category}`);
    }

    return Array.from(terms)
      .map((term) => normalizeSearchText(term))
      .filter((term) => term.length >= 2 && term !== normalizedTitle)
      .map((term) => ({
        type: 'query',
        term: titleCaseTerm(term),
        title: titleCaseTerm(term),
        normalized: term,
        compact: compactSearchText(term),
        url: `/search?q=${encodeURIComponent(term)}`,
        count: entry.count || 1,
        weight: Math.max(80, Number(entry.weight || 0) * 0.85),
        sourceProductHandle: entry.handle || ''
      }));
  };

  const getDerivedProductSuggestionMatches = (state, query, limit) => {
    const productMatches = getDictionaryMatches(state, query, ['product'], 36);
    const merged = new Map();

    productMatches.forEach((entry) => {
      derivedProductSuggestionTerms(entry).forEach((termEntry) => {
        const dictionaryScore = scoreDictionaryEntry(termEntry, query);
        if (dictionaryScore <= 0) return;

        const existing = merged.get(termEntry.normalized);
        const next = {
          ...termEntry,
          dictionaryScore: dictionaryScore + Math.min(40, Number(entry.dictionaryScore || 0) / 20)
        };

        if (!existing || next.dictionaryScore > existing.dictionaryScore) {
          merged.set(termEntry.normalized, next);
        }
      });
    });

    return Array.from(merged.values())
      .sort((a, b) => {
        return b.dictionaryScore - a.dictionaryScore
          || String(a.term).localeCompare(String(b.term));
      })
      .slice(0, limit);
  };

  const getAutocompleteMatches = (state, query, limit) => {
    const matches = [
      ...getDictionaryMatches(state, query, ['query', 'brand'], limit),
      ...getDerivedProductSuggestionMatches(state, query, limit)
    ];
    const seen = new Set();

    return matches
      .sort((a, b) => {
        return b.dictionaryScore - a.dictionaryScore
          || String(a.term).localeCompare(String(b.term));
      })
      .filter((entry) => {
        const key = normalizeSearchText(entry.term);

        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, limit);
  };

  const productQueryFromSuggestions = (state, suggestions, fallbackQuery) => {
    if (hasLiteralProductTitleMatch(state, fallbackQuery)) {
      return String(fallbackQuery || '').trim();
    }

    const suggestion = suggestions.find((entry) => ['query', 'brand'].includes(entry.type));

    return String(suggestion?.term || fallbackQuery || '').trim();
  };

  const setStatus = (form, message = '') => {
    const status = form.querySelector('[data-header-search-status]');

    if (status) status.textContent = message;
  };

  const updateModalSearchOffset = (form) => {
    if (!isModalSearch(form)) return;

    const header = form.closest('[data-header-v2]');
    const masthead = header?.querySelector('.site-header-v2__masthead');
    const stickyBar = header?.querySelector('[data-header-v2-sticky-bar]') || header;
    const anchor = masthead || stickyBar;
    const bottom = anchor?.getBoundingClientRect?.().bottom || 0;

    form.style.setProperty('--header-search-modal-top', `${Math.max(0, bottom)}px`);
  };

  const syncModalSearchClass = () => {
    const isOpen = Boolean(document.querySelector('[data-header-search-mode="modal"].header-search-is-open'));

    document.documentElement.classList.toggle('header-search-modal-is-open', isOpen);
    document.body.classList.toggle('header-search-modal-is-open', isOpen);
  };

  const setCloseButtonHidden = (form, hidden) => {
    const closeButton = form.querySelector('[data-header-search-close]');

    if (closeButton) closeButton.hidden = hidden;
  };

  const isOpenMobileHeaderSearchForm = (form) => {
    const panel = form.closest('[data-header-v2-mobile-search]');

    return Boolean(panel && !panel.hidden);
  };

  const isEventInsideForm = (event, form) => {
    if (event.composedPath?.().includes(form)) return true;

    const target = event.target instanceof Element ? event.target : null;

    return Boolean(target && form.contains(target));
  };

  const wasJustOpened = (form) => {
    const state = formStates.get(form);

    return Boolean(state?.openedAt && Date.now() - state.openedAt < outsideClickOpenGraceMs);
  };

  const closeNavDropdowns = (form) => {
    const header = form.closest('header');

    if (!header) return;

    header.querySelectorAll('[data-site-nav-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });

    header.querySelectorAll('[data-site-nav-dropdown]').forEach((dropdown) => {
      dropdown.hidden = true;
    });

    header.querySelectorAll('.site-nav').forEach((nav) => {
      nav.classList.remove('site-nav--dropdown-open');
    });
  };

  const closeDropdown = (form, { force = false } = {}) => {
    const dropdown = form.querySelector('[data-header-search-dropdown]');
    const input = form.querySelector('[data-header-search-input]');
    const state = formStates.get(form);

    if (!dropdown) return;

    // The mobile panel owns its close state. In WebKit the click that opens
    // the panel can reach this document-level outside-click handler before
    // the programmatic input focus has fully settled. Closing only the inner
    // dropdown at that point leaves an open, blank search overlay.
    if (!force && isOpenMobileHeaderSearchForm(form)) return;

    dropdown.hidden = true;
    form.classList.remove('header-search-is-open');
    input?.setAttribute('aria-expanded', 'false');
    setCloseButtonHidden(form, true);
    state?.abortController?.abort();
    if (state) {
      state.abortController = null;
      state.openedAt = 0;
    }
    syncModalSearchClass();
  };

  const closeOtherDropdowns = (currentForm) => {
    document.querySelectorAll('[data-header-search]').forEach((form) => {
      if (form !== currentForm) closeDropdown(form, { force: true });
    });
  };

  const openDropdown = (form) => {
    const dropdown = form.querySelector('[data-header-search-dropdown]');
    const input = form.querySelector('[data-header-search-input]');

    if (!dropdown || !input) return;

    closeOtherDropdowns(form);
    closeNavDropdowns(form);
    updateModalSearchOffset(form);
    const state = formStates.get(form);
    const requestedOpen = form.dataset.headerSearchRequestedOpen === 'true';

    if (state && (dropdown.hidden || requestedOpen)) {
      state.openedAt = Date.now();
    }

    delete form.dataset.headerSearchRequestedOpen;

    dropdown.hidden = false;
    form.classList.add('header-search-is-open');
    setCloseButtonHidden(form, false);
    syncModalSearchClass();
    input.setAttribute('aria-expanded', 'true');
  };

  const renderTrendingTerms = (form, terms, { heading = 'Trending Searches', query = '' } = {}) => {
    const list = form.querySelector('[data-header-search-trending-list]');
    const section = form.querySelector('[data-header-search-trending]');
    const headingElement = form.querySelector('[data-header-search-trending-heading]');

    if (!list || !section) return;

    const normalizedTerms = terms.map((item) => {
      if (typeof item === 'string') {
        return {
          term: item,
          url: `/search?q=${encodeURIComponent(item)}`,
          type: 'query'
        };
      }

      const term = String(item?.term || item?.title || '').trim();

      return {
        term,
        url: item?.type === 'collection' && item?.url ? item.url : `/search?q=${encodeURIComponent(term)}`,
        type: item?.type || 'query'
      };
    }).filter((item) => item.term);
    const seen = new Set();
    const uniqueTerms = normalizedTerms.filter((item) => {
      const key = normalizeSearchText(item.term);

      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, suggestedSearchLimit);

    if (headingElement) headingElement.textContent = heading;

    list.innerHTML = uniqueTerms.map((item) => `
      <a
        class="header-search-dropdown__term"
        href="${escapeHtml(item.url)}"
        data-header-search-trending-link
        data-header-search-suggestion-type="${escapeHtml(item.type)}"
        data-header-search-query="${escapeHtml(query || item.term)}"
      >
        ${escapeHtml(item.term)}
      </a>
    `).join('');

    section.hidden = uniqueTerms.length === 0;
  };

  const renderCollections = (form, collections, query) => {
    const section = form.querySelector('[data-header-search-collections-section]');
    const list = form.querySelector('[data-header-search-collections]');

    if (!section || !list) return;

    list.innerHTML = collections.map((collection, index) => {
      const count = Number(collection.productCount ?? collection.productsCount ?? collection.count ?? 0);

      return `
        <a
          class="header-search-dropdown__collection"
          href="${escapeHtml(getCollectionUrl(collection))}"
          data-header-search-result-link
          data-header-search-result-type="collection"
          data-header-search-result-position="${index + 1}"
          data-header-search-collection-handle="${escapeHtml(collection.handle || '')}"
          data-header-search-query="${escapeHtml(query)}"
        >
          <span class="header-search-dropdown__collection-title">${escapeHtml(collection.title || collection.handle || 'Collection')}</span>
          ${count > 0 ? `<span class="header-search-dropdown__collection-meta">${count} styles</span>` : ''}
        </a>
      `;
    }).join('');

    section.hidden = collections.length === 0;
  };

  const renderProducts = (form, products, query) => {
    const section = form.querySelector('[data-header-search-products-section]');
    const list = form.querySelector('[data-header-search-products]');
    const heading = form.querySelector('[data-header-search-products-heading]');

    if (!section || !list) return;

    if (heading) {
      heading.textContent = query ? 'Products' : "We think you'll love";
    }

    list.removeAttribute('aria-busy');
    list.innerHTML = products.map((product, index) => {
      const image = getImage(product);
      const currencyCode = product?.priceRange?.minVariantPrice?.currencyCode || product?.price?.currencyCode || 'USD';
      const price = moneyAmount(product?.priceRange?.minVariantPrice ?? product?.price);
      const compareAtPrice = moneyAmount(product?.compareAtPriceRange?.maxVariantPrice);
      const maxPrice = moneyAmount(product?.priceRange?.maxVariantPrice ?? price);
      const productId = product.numericId || normalizeNumericId(product.id) || '';
      const priceVariant = (product?.variants || []).find((variant) => moneyAmount(variant?.price) === price);
      const variantId = String(priceVariant?.id || priceVariant?.numericId || product?.selectedVariantId || '').match(/(\d+)$/)?.[1] || '';
      const onSale = compareAtPrice > price;
      const showFrom = maxPrice > price;

      return `
        <a
          class="header-search-dropdown__product"
          href="${escapeHtml(getProductUrl(product))}"
          data-header-search-result-link
          data-header-search-result-type="product"
          data-header-search-result-position="${index + 1}"
          data-header-search-product-id="${escapeHtml(product.numericId || product.id || '')}"
          data-header-search-product-handle="${escapeHtml(product.handle || '')}"
          data-header-search-product-title="${escapeHtml(product.title || '')}"
          data-header-search-query="${escapeHtml(query)}"
        >
          <span class="header-search-dropdown__product-image" aria-hidden="${image?.url ? 'false' : 'true'}">
            ${image?.url ? `
              <img
                class="image-loading"
                src="${escapeHtml(withImageWidth(image.url, 220))}"
                width="${image.width || 220}"
                height="${image.height || 330}"
                alt="${escapeHtml(image.altText || product.title || 'Product')}"
                loading="lazy"
                decoding="async"
              >
            ` : ''}
          </span>
          <span class="header-search-dropdown__product-content">
            <span class="header-search-dropdown__product-title">${escapeHtml(product.title || 'Product')}</span>
            ${price > 0 ? `<span class="header-search-dropdown__product-price price${onSale ? ' price--on-sale' : ''}" data-product-price data-product-id="${escapeHtml(productId)}" data-variant-id="${escapeHtml(variantId)}" data-base-price-cents="${Math.round(price * 100)}" data-compare-at-price-cents="${Math.round((onSale ? compareAtPrice : 0) * 100)}" data-price-show-from="${showFrom ? 'true' : 'false'}">${onSale ? `<span class="visually-hidden" data-price-compare-label>Regular price</span><s class="price__compare" data-price-compare>${formatMoney(compareAtPrice, currencyCode)}</s>` : ''}<span class="visually-hidden" data-price-current-label>${onSale ? 'Sale price' : 'Regular price'}</span><span class="price__current" data-price-current>${showFrom ? 'From ' : ''}${formatMoney(price, currencyCode)}</span></span>` : ''}
          </span>
        </a>
      `;
    }).join('');

    section.hidden = products.length === 0;
  };

  const renderProductSkeletons = (form, count = 4, query = '') => {
    const section = form.querySelector('[data-header-search-products-section]');
    const list = form.querySelector('[data-header-search-products]');
    const heading = form.querySelector('[data-header-search-products-heading]');

    if (!section || !list) return;

    if (heading) heading.textContent = query ? 'Products' : "We think you'll love";

    const cardCount = Math.max(1, Math.min(Number(count) || 4, 6));

    list.setAttribute('aria-busy', 'true');
    list.innerHTML = Array.from({ length: cardCount }, () => `
      <div class="header-search-dropdown__product header-search-dropdown__product--skeleton" aria-hidden="true">
        <span class="header-search-dropdown__product-image header-search-dropdown__product-skeleton-media"></span>
        <span class="header-search-dropdown__product-content">
          <span class="header-search-dropdown__product-skeleton-line header-search-dropdown__product-skeleton-line--title"></span>
          <span class="header-search-dropdown__product-skeleton-line header-search-dropdown__product-skeleton-line--title-short"></span>
          <span class="header-search-dropdown__product-skeleton-line header-search-dropdown__product-skeleton-line--price"></span>
        </span>
      </div>
    `).join('');
    section.hidden = false;
  };

  const renderEmptyState = (form, query, isEmpty) => {
    const empty = form.querySelector('[data-header-search-empty]');
    const viewAll = form.querySelector('[data-header-search-view-all]');

    if (!empty) return;

    empty.hidden = !isEmpty;

    if (viewAll) {
      viewAll.href = query ? `/search?q=${encodeURIComponent(query)}` : '/search';
      viewAll.textContent = query ? `Search for "${query}"` : 'Search the site';
    }
  };

  const showTrendingOnly = (form, query = '') => {
    const state = formStates.get(form);

    if (!state) return;

    state.suggestedProductQuery = '';
    renderTrendingTerms(form, getActiveTrendingTerms(state));
    renderCollections(form, getFallbackCollectionMatches(state, Number(form.dataset.headerSearchCollectionLimit || 4)), '');

    if (query) {
      renderProducts(form, [], query);
    } else if (state.featuredProductsStatus === 'ready') {
      renderProducts(form, state.workerFeaturedProducts, '');
    } else if (state.featuredProductsStatus === 'idle' || state.featuredProductsStatus === 'loading') {
      renderProductSkeletons(form, Number(form.dataset.headerSearchProductLimit || 4));
    } else {
      renderProducts(form, state.featuredProducts, '');
    }

    renderEmptyState(form, query, Boolean(query));
    setStatus(form, '');
  };

  const getFeaturedProductsRequestKey = (form) => {
    const endpoint = normalizeEndpoint(form.dataset.headerSearchEndpoint);
    const enabled = form.dataset.headerSearchEnabled === 'true';
    const collectionId = form.dataset.headerSearchFeaturedCollectionId || '';
    const collectionHandle = form.dataset.headerSearchFeaturedCollectionHandle || '';
    const productLimit = Number(form.dataset.headerSearchProductLimit || 4);

    if (
      !enabled
      || !endpoint
      || !collectionHandle
      || !('fetch' in window)
      || window.WobWorkerMarket?.isPricingCompatible?.() === false
    ) return '';

    return [endpoint, collectionId, collectionHandle, productLimit, 'recommended', 'header-search-default'].join('|');
  };

  const requestFeaturedProducts = (form) => {
    const requestKey = getFeaturedProductsRequestKey(form);

    if (!requestKey) return Promise.reject(new Error('header-search-featured-products-unavailable'));
    if (featuredProductRequests.has(requestKey)) return featuredProductRequests.get(requestKey);

    const endpoint = normalizeEndpoint(form.dataset.headerSearchEndpoint);
    const collectionId = form.dataset.headerSearchFeaturedCollectionId || '';
    const collectionHandle = form.dataset.headerSearchFeaturedCollectionHandle || '';
    const productLimit = Number(form.dataset.headerSearchProductLimit || 4);
    const controller = new AbortController();
    const timeout = createRequestTimeout(controller, featuredProductsRequestTimeoutMs);
    const request = fetch(`${endpoint}/collections/products`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        collectionId,
        collectionHandle,
        pageSize: productLimit,
        sortKey: 'recommended',
        filters: [],
        source: 'header-search-default'
      }),
      signal: controller.signal
    }).then(async (response) => {
      const payload = await response.json().catch(() => null);

      if (!response.ok || !payload?.ok || payload?.fallback?.recommended) {
        throw new Error(payload?.fallback?.reason || 'header-search-featured-products-unavailable');
      }

      const products = Array.isArray(payload.products) ? payload.products.slice(0, productLimit) : [];

      if (!products.length) throw new Error('header-search-featured-products-empty');

      return products;
    }).finally(() => timeout.clear());

    featuredProductRequests.set(requestKey, request);
    request.catch(() => featuredProductRequests.delete(requestKey));

    return request;
  };

  const loadFeaturedProducts = async (form) => {
    const state = formStates.get(form);

    if (!state || state.featuredProductsStatus !== 'idle') return;

    if (!getFeaturedProductsRequestKey(form)) {
      state.featuredProductsStatus = 'fallback';
      return;
    }

    state.featuredProductsStatus = 'loading';

    try {
      state.workerFeaturedProducts = await requestFeaturedProducts(form);
      state.featuredProductsStatus = 'ready';
    } catch (error) {
      state.workerFeaturedProducts = [];
      state.featuredProductsStatus = 'fallback';
    }

    const input = form.querySelector('[data-header-search-input]');

    if (!input?.value.trim()) {
      showTrendingOnly(form);
    } else if (!state.abortController) {
      updateDictionaryPreview(form, input.value);
    }
  };

  const requestTrending = async (form, signal) => {
    const endpoint = normalizeEndpoint(form.dataset.headerSearchEndpoint);
    const enabled = form.dataset.headerSearchEnabled === 'true';

    if (!enabled || !endpoint || !('fetch' in window)) return null;

    const response = await fetch(`${endpoint}/search/trending?window=${encodeURIComponent(trendingWindow)}&limit=${suggestedSearchLimit}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      signal
    });

    if (!response.ok) throw new Error('header-search-trending-unavailable');

    return response.json();
  };

  const requestDictionary = async (form, signal) => {
    const endpoint = normalizeEndpoint(form.dataset.headerSearchEndpoint);
    const enabled = form.dataset.headerSearchEnabled === 'true';

    if (!enabled || !endpoint || !('fetch' in window)) return [];

    const cached = dictionaryCache.get(endpoint);

    if (cached?.entries) return cached.entries;
    if (cached?.promise) return cached.promise;

    const promise = fetch(`${endpoint}/search/dictionary`, {
      cache: 'force-cache',
      headers: { Accept: 'application/json' },
      signal
    })
      .then((response) => {
        if (!response.ok) throw new Error('header-search-dictionary-unavailable');
        return response.json();
      })
      .then((payload) => {
        const entries = normalizeDictionaryEntries(payload);
        dictionaryCache.set(endpoint, { entries });

        return entries;
      })
      .catch((error) => {
        dictionaryCache.delete(endpoint);
        throw error;
      });

    dictionaryCache.set(endpoint, { promise });
    return promise;
  };

  const requestSuggestions = async (form, query, signal, requestId) => {
    const endpoint = normalizeEndpoint(form.dataset.headerSearchEndpoint);
    const enabled = form.dataset.headerSearchEnabled === 'true';

    if (
      !enabled
      || !endpoint
      || !('fetch' in window)
      || window.WobWorkerMarket?.isPricingCompatible?.() === false
    ) return null;

    const response = await fetch(`${endpoint}/search`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        query,
        normalizedQuery: query.toLowerCase(),
        productLimit: Number(form.dataset.headerSearchProductLimit || 6),
        collectionLimit: Number(form.dataset.headerSearchCollectionLimit || 4),
        include: ['products', 'collections'],
        source: 'header-search',
        analytics: {
          requestId,
          sessionId: getWorkerSessionId()
        }
      }),
      signal
    });
    const payload = await response.json().catch(() => null);

    if (!response.ok || !payload || payload?.fallback?.recommended) {
      throw new Error(payload?.fallback?.reason || 'header-search-unavailable');
    }

    return payload;
  };

  const requestPredictiveSuggestions = async (form, query, signal) => {
    if (!isModalSearch(form) || !('fetch' in window)) return null;

    const productLimit = Number(form.dataset.headerSearchProductLimit || 4);
    const collectionLimit = Number(form.dataset.headerSearchCollectionLimit || 4);
    const limit = Math.max(productLimit, collectionLimit, 4);
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product,collection,query',
      'resources[limit]': String(limit),
      'resources[options][fields]': 'title,product_type,variants.title,vendor,body,tag',
      'resources[options][unavailable_products]': 'last'
    });
    const response = await fetch(`/search/suggest.json?${params.toString()}`, {
      headers: { Accept: 'application/json' },
      signal
    });

    if (!response.ok) return null;

    return response.json().catch(() => null);
  };

  const loadTrending = async (form) => {
    const state = formStates.get(form);

    if (!state || state.trendingLoading) return;

    if (state.trendingLoaded) {
      renderTrendingTerms(form, getActiveTrendingTerms(state));
      return;
    }

    state.trendingLoading = true;
    renderTrendingTerms(form, getActiveTrendingTerms(state));

    const controller = new AbortController();
    const timeout = createRequestTimeout(controller, trendingRequestTimeoutMs);

    try {
      const payload = await requestTrending(form, controller.signal);
      const terms = normalizeTrendingTerms(payload, state.fallbackTerms);

      state.trendingTerms = terms;
      state.trendingLoaded = true;
      renderTrendingTerms(form, getActiveTrendingTerms(state));
    } catch (error) {
      state.trendingLoaded = false;
      renderTrendingTerms(form, getActiveTrendingTerms(state));
    } finally {
      state.trendingLoading = false;
      timeout.clear();
    }
  };

  const updateDictionaryPreview = (form, query, { renderProductPreview = true } = {}) => {
    const state = formStates.get(form);
    const normalizedQuery = String(query || '').trim();

    if (!state || normalizedQuery.length < 1) return false;

    const productLimit = Number(form.dataset.headerSearchProductLimit || 6);
    const collectionLimit = Number(form.dataset.headerSearchCollectionLimit || 4);
    const autocompleteLimit = suggestedSearchLimit;
    const autocompleteMatches = getAutocompleteMatches(state, normalizedQuery, autocompleteLimit);
    const collectionMatches = getCollectionMatches(state, normalizedQuery, collectionLimit);
    const renderedAutocompleteMatches = autocompleteMatches.length ? autocompleteMatches : getActiveTrendingTerms(state);
    const renderedCollectionMatches = collectionMatches.length ? collectionMatches : getFallbackCollectionMatches(state, collectionLimit);
    const productPreviewQuery = productQueryFromSuggestions(state, autocompleteMatches, normalizedQuery);
    const featuredProductsLoading = state.featuredProductsStatus === 'idle'
      || state.featuredProductsStatus === 'loading';
    const previewProducts = renderProductPreview && !featuredProductsLoading
      ? getDefaultFeaturedProducts(state).slice(0, productLimit)
      : [];
    const hasMatches = renderedAutocompleteMatches.length > 0
      || renderedCollectionMatches.length > 0
      || (renderProductPreview && featuredProductsLoading)
      || previewProducts.length > 0;

    if (!hasMatches) {
      state.suggestedProductQuery = '';
      return false;
    }

    state.suggestedProductQuery = productPreviewQuery;

    renderTrendingTerms(form, renderedAutocompleteMatches, {
      heading: 'Trending Searches',
      query: normalizedQuery
    });
    renderCollections(form, renderedCollectionMatches, normalizedQuery);

    if (renderProductPreview && state.workerRenderedQuery !== normalizedQuery) {
      // Dictionary entries are ranking metadata, not a storefront pricing source.
      // Keep the current worker-backed recommendation rail visible until search responds.
      if (featuredProductsLoading) {
        renderProductSkeletons(form, productLimit);
      } else {
        renderProducts(form, previewProducts, '');
      }
      renderEmptyState(form, normalizedQuery, false);
    }

    setStatus(form, '');
    return true;
  };

  const loadDictionary = async (form, { refreshPreview = false, renderProductPreview = true } = {}) => {
    const state = formStates.get(form);

    if (!state || state.dictionaryLoading) return;
    if (state.dictionaryLoaded) {
      if (refreshPreview) {
        const input = form.querySelector('[data-header-search-input]');
        updateDictionaryPreview(form, input?.value || '', { renderProductPreview });
      } else {
        const input = form.querySelector('[data-header-search-input]');

        if (!input?.value.trim()) showTrendingOnly(form);
      }
      return;
    }

    state.dictionaryLoading = true;

    const controller = new AbortController();
    const timeout = createRequestTimeout(controller, dictionaryRequestTimeoutMs);

    try {
      state.dictionaryEntries = await requestDictionary(form, controller.signal);
      state.dictionaryLoaded = true;

      if (refreshPreview) {
        const input = form.querySelector('[data-header-search-input]');
        updateDictionaryPreview(form, input?.value || '', { renderProductPreview });
      }
    } catch (error) {
      state.dictionaryLoaded = false;
    } finally {
      state.dictionaryLoading = false;
      timeout.clear();
    }
  };

  const runSuggestions = async (form, query) => {
    const state = formStates.get(form);
    const normalizedQuery = String(query || '').trim();

    if (!state) return;

    state.abortController?.abort();
    state.abortController = null;

    if (normalizedQuery.length < minimumQueryLength) {
      if (normalizedQuery) {
        if (!updateDictionaryPreview(form, normalizedQuery)) {
          const activeTerms = getActiveTrendingTerms(state);
          const filteredTerms = activeTerms.filter((term) => {
            return normalizeSearchText(term).includes(normalizeSearchText(normalizedQuery));
          });

          renderTrendingTerms(form, filteredTerms.length ? filteredTerms : activeTerms, {
            heading: 'Suggestions',
            query: normalizedQuery
          });
          renderCollections(form, getFallbackCollectionMatches(state, Number(form.dataset.headerSearchCollectionLimit || 4)), normalizedQuery);
          renderProducts(form, [], normalizedQuery);
          renderEmptyState(form, normalizedQuery, false);
        }

        loadDictionary(form, { refreshPreview: true });
      } else {
        showTrendingOnly(form);
      }

      setStatus(form, '');
      return;
    }

    const controller = new AbortController();
    const requestId = getRequestId();
    const productLimit = Number(form.dataset.headerSearchProductLimit || 6);

    state.abortController = controller;
    state.workerRenderedQuery = state.workerRenderedQuery === normalizedQuery ? state.workerRenderedQuery : '';
    updateDictionaryPreview(form, normalizedQuery, { renderProductPreview: false });
    loadDictionary(form, { refreshPreview: true, renderProductPreview: false });
    renderEmptyState(form, normalizedQuery, false);
    renderProductSkeletons(form, productLimit, normalizedQuery);
    setStatus(form, '');

    const timeout = createRequestTimeout(controller, suggestionRequestTimeoutMs);

    try {
      let payload = null;
      const productQuery = state.suggestedProductQuery || normalizedQuery;

      try {
        payload = await requestSuggestions(form, productQuery, controller.signal, requestId);
      } catch (error) {
        if (controller.signal.aborted && !timeout.timedOut) return;
        if (timeout.timedOut) throw error;

        payload = await requestPredictiveSuggestions(form, productQuery, controller.signal).catch(() => null);
      }

      if (!payload && isModalSearch(form) && !controller.signal.aborted) {
        payload = await requestPredictiveSuggestions(form, productQuery, controller.signal).catch(() => null);
      }

      if (controller.signal.aborted) return;
      timeout.clear();
      if (!payload) {
        if (!updateDictionaryPreview(form, normalizedQuery)) {
          showTrendingOnly(form, normalizedQuery);
        }
        return;
      }

      const collectionLimit = Number(form.dataset.headerSearchCollectionLimit || 4);
      let responseProducts = normalizeProducts(payload);
      responseProducts = await (
        window.WobWorkerMarket?.localizeProducts?.(responseProducts)
        || Promise.resolve(responseProducts)
      );
      const collections = filterCollectionsForMenu(state, normalizeCollections(payload)).slice(0, collectionLimit);
      const querySuggestions = normalizeQuerySuggestions(payload);
      const useDefaultProductRail = responseProducts.length === 0;

      if (
        useDefaultProductRail
        && (state.featuredProductsStatus === 'idle' || state.featuredProductsStatus === 'loading')
      ) {
        state.featuredProductsStatus = 'loading';

        try {
          state.workerFeaturedProducts = await requestFeaturedProducts(form);
          state.featuredProductsStatus = 'ready';
        } catch (error) {
          state.workerFeaturedProducts = [];
          state.featuredProductsStatus = 'fallback';
        }

        if (controller.signal.aborted) return;
      }

      let products = useDefaultProductRail
        ? getDefaultFeaturedProducts(state).slice(0, productLimit)
        : selectProductsForQuery(state, responseProducts, normalizedQuery, productLimit);
      products = await (
        window.WobWorkerMarket?.localizeProducts?.(products)
        || Promise.resolve(products)
      );
      const autocompleteMatches = getAutocompleteMatches(state, normalizedQuery, suggestedSearchLimit);
      const collectionMatches = getCollectionMatches(state, normalizedQuery, collectionLimit);
      const renderedSearchTerms = querySuggestions.length
        ? querySuggestions
        : (autocompleteMatches.length ? autocompleteMatches : getActiveTrendingTerms(state));
      const renderedCollections = collections.length
        ? collections
        : (collectionMatches.length ? collectionMatches : getFallbackCollectionMatches(state, collectionLimit));
      const isEmpty = products.length === 0 && collections.length === 0;

      if (isEmpty && updateDictionaryPreview(form, normalizedQuery)) {
        setStatus(form, '');
        state.workerRenderedQuery = normalizedQuery;
        return;
      }

      renderTrendingTerms(form, renderedSearchTerms, {
        heading: querySuggestions.length ? 'Suggestions' : 'Trending Searches',
        query: normalizedQuery
      });

      renderCollections(form, renderedCollections, normalizedQuery);
      renderProducts(form, products, useDefaultProductRail ? '' : normalizedQuery);
      renderEmptyState(form, normalizedQuery, isEmpty && !renderedCollections.length);
      setStatus(form, isEmpty && !renderedCollections.length ? `No quick results found for ${normalizedQuery}.` : '');
      state.workerRenderedQuery = normalizedQuery;

    } catch (error) {
      if (timeout.timedOut || !controller.signal.aborted) {
        setStatus(form, '');
        if (!updateDictionaryPreview(form, normalizedQuery)) {
          showTrendingOnly(form, normalizedQuery);
        }
      }
    } finally {
      timeout.clear();

      if (state.abortController === controller) {
        state.abortController = null;
      }
    }
  };

  const scheduleSuggestions = (form) => {
    const state = formStates.get(form);
    const input = form.querySelector('[data-header-search-input]');

    if (!state || !input) return;

    window.clearTimeout(state.debounceTimer);
    state.debounceTimer = window.setTimeout(() => {
      runSuggestions(form, input.value);
    }, Number(form.dataset.headerSearchDebounceMs || 250));
  };

  const bindForm = (form) => {
    if (initializedForms.has(form)) return;

    const input = form.querySelector('[data-header-search-input]');
    const dropdown = form.querySelector('[data-header-search-dropdown]');

    if (!input || !dropdown) return;

    initializedForms.add(form);
    const menuCollections = getMenuCollections(form);

    formStates.set(form, {
      abortController: null,
      debounceTimer: null,
      dictionaryEntries: [],
      dictionaryLoaded: false,
      dictionaryLoading: false,
      fallbackTerms: getFallbackTerms(form),
      featuredProducts: getFeaturedProducts(form),
      featuredProductsStatus: getFeaturedProductsRequestKey(form) ? 'idle' : 'fallback',
      menuCollectionHandles: new Set(menuCollections.map((collection) => collection.handle).filter(Boolean)),
      menuCollectionNumericIds: new Set(menuCollections.map((collection) => collection.numericId).filter(Boolean)),
      menuCollections,
      openedAt: 0,
      suggestedProductQuery: '',
      trendingLoaded: false,
      trendingLoading: false,
      trendingTerms: [],
      workerFeaturedProducts: [],
      workerRenderedQuery: ''
    });
    showTrendingOnly(form);
    setCloseButtonHidden(form, true);
    window.setTimeout(() => loadFeaturedProducts(form), 0);
    window.setTimeout(() => loadTrending(form), 0);
    window.setTimeout(() => loadDictionary(form), 0);

    const openSearch = () => {
      openDropdown(form);
      loadFeaturedProducts(form);
      loadTrending(form);
      loadDictionary(form, { refreshPreview: true });

      if (input.value.trim().length >= minimumQueryLength) {
        updateDictionaryPreview(form, input.value);
        scheduleSuggestions(form);
      } else if (input.value.trim()) {
        updateDictionaryPreview(form, input.value);
      } else {
        showTrendingOnly(form);
      }
    };

    input.addEventListener('focus', openSearch);
    form.addEventListener('wob:header-search:open', openSearch);

    input.addEventListener('input', () => {
      openDropdown(form);
      const state = formStates.get(form);

      if (state) state.workerRenderedQuery = '';
      updateDictionaryPreview(form, input.value, { renderProductPreview: false });
      loadDictionary(form, { refreshPreview: true, renderProductPreview: false });
      scheduleSuggestions(form);
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDropdown(form, { force: true });
        input.blur();
        return;
      }

      if (event.key !== 'ArrowDown') return;

      const firstLink = dropdown.querySelector('a[href]');

      if (!firstLink || dropdown.hidden) return;

      event.preventDefault();
      firstLink.focus();
    });

    form.addEventListener('focusout', () => {
      if (isOpenMobileHeaderSearchForm(form)) {
        return;
      }

      window.setTimeout(() => {
        if (!form.contains(document.activeElement)) {
          closeDropdown(form);
        }
      }, 0);
    });

    form.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const closeButton = target?.closest('[data-header-search-close]');
      const trendingLink = target?.closest('[data-header-search-trending-link]');

      if (closeButton) {
        closeDropdown(form, { force: true });
        input.blur();
        return;
      }

      if (trendingLink) {
        return;
      }
    });

    // On slower mobile connections header-v2 can open and focus the panel before
    // this deferred search bundle has finished binding. Recover that already-open
    // state immediately so the first open receives Worker recommendations.
    if (
      form.dataset.headerSearchRequestedOpen === 'true'
      || isOpenMobileHeaderSearchForm(form)
      || document.activeElement === input
    ) {
      openSearch();
    }
  };

  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-header-search]').forEach((form) => {
      if (isEventInsideForm(event, form) || wasJustOpened(form)) {
        return;
      }

      if (!form.querySelector('[data-header-search-dropdown]')?.hidden) {
        closeDropdown(form);
      }
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    document.querySelectorAll('[data-header-search]').forEach((form) => {
      closeDropdown(form, { force: true });
    });
  });

  window.addEventListener('resize', () => {
    document.querySelectorAll('[data-header-search-mode="modal"].header-search-is-open').forEach(updateModalSearchOffset);
  });

  const init = (root = document) => {
    root.querySelectorAll?.('[data-header-search]').forEach(bindForm);
  };

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
